import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { SiteProvider } from "./contexts/SiteContext";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import CartDrawer from "./components/CartDrawer";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Account from "./pages/Account";
import OrderTracking from "./pages/OrderTracking";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminCoupons from "./pages/admin/AdminCoupons";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminContent from "./pages/admin/AdminContent";
import AdminPages from "./pages/admin/AdminPages";
import StaticPage from "./pages/StaticPage";
import Manual from "./pages/Manual";

function ProtectedRoute({ children, adminOnly = false }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading)
        return (
            <div className="flex min-h-screen items-center justify-center bg-obsidian">
                <span className="font-display text-2xl text-gold">Loading…</span>
            </div>
        );
    if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
    if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
    return children;
}

function StoreShell() {
    return (
        <div className="App flex min-h-screen flex-col">
            <Navbar />
            <CartDrawer />
            <main className="flex-1">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/product/:id" element={<ProductDetail />} />
                    <Route path="/p/:slug" element={<StaticPage />} />
                    <Route path="/manual" element={<Manual />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route
                        path="/checkout"
                        element={
                            <ProtectedRoute>
                                <Checkout />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/order/success" element={<OrderSuccess />} />
                    <Route
                        path="/order/:id"
                        element={
                            <ProtectedRoute>
                                <OrderTracking />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/account"
                        element={
                            <ProtectedRoute>
                                <Account />
                            </ProtectedRoute>
                        }
                    />
                </Routes>
            </main>
            <Footer />
        </div>
    );
}

function App() {
    return (
        <AuthProvider>
            <SiteProvider>
                <CartProvider>
                    <BrowserRouter>
                    <Toaster
                        position="top-center"
                        toastOptions={{
                            style: {
                                background: "#141414",
                                color: "#FDFBF7",
                                border: "1px solid #2A2A2A",
                                fontFamily: "Outfit",
                            },
                        }}
                    />
                    <Routes>
                        <Route
                            path="/admin/*"
                            element={
                                <ProtectedRoute adminOnly>
                                    <AdminLayout>
                                        <Routes>
                                            <Route index element={<AdminDashboard />} />
                                            <Route path="products" element={<AdminProducts />} />
                                            <Route path="orders" element={<AdminOrders />} />
                                            <Route path="content" element={<AdminContent />} />
                                            <Route path="pages" element={<AdminPages />} />
                                            <Route path="coupons" element={<AdminCoupons />} />
                                            <Route path="customers" element={<AdminCustomers />} />
                                        </Routes>
                                    </AdminLayout>
                                </ProtectedRoute>
                            }
                        />
                        <Route path="/*" element={<StoreShell />} />
                    </Routes>
                    </BrowserRouter>
                </CartProvider>
            </SiteProvider>
        </AuthProvider>
    );
}

export default App;

